import type { Feed } from './types'

const KEY_NAME = 'inktime.feedKey'
const FEED_CACHE = 'inktime.feedCache'
const ETAG_CACHE = 'inktime.feedEtag'
const MEDIA_CACHE = 'inktime-media-v3'

export const getFeedKey = () => localStorage.getItem(KEY_NAME) ?? ''
export const saveFeedKey = (key: string) => localStorage.setItem(KEY_NAME, key.trim())
export const clearFeedKey = () => localStorage.removeItem(KEY_NAME)

export function extractFeedKey(value: string): string {
  const text = value.trim()
  const marker = '#/subscribe/'
  const index = text.indexOf(marker)
  return index >= 0 ? decodeURIComponent(text.slice(index + marker.length).split(/[/?#]/)[0]) : text
}

export function cachedFeed(): Feed | null {
  try {
    return JSON.parse(localStorage.getItem(FEED_CACHE) ?? 'null') as Feed | null
  } catch {
    return null
  }
}

export async function loadFeed(key: string): Promise<Feed> {
  const etag = localStorage.getItem(ETAG_CACHE)
  const response = await fetch('/api/v1/feed', {
    headers: {
      Authorization: `Bearer ${key}`,
      ...(etag ? { 'If-None-Match': etag } : {}),
    },
  })
  if (response.status === 304) {
    const cached = cachedFeed()
    if (cached) {
      try {
        await cacheFeedAssets(cached)
        return cached
      } catch {
        // The browser may have evicted media while retaining the Feed/ETag.
        // Fetch once without the ETag to renew the signed asset URLs.
        localStorage.removeItem(ETAG_CACHE)
        return loadFeed(key)
      }
    }
    localStorage.removeItem(ETAG_CACHE)
    return loadFeed(key)
  }
  if (!response.ok) throw new Error(response.status === 401 ? '订阅密钥无效' : `获取内容失败 (${response.status})`)
  const feed = (await response.json()) as Feed
  await cacheFeedAssets(feed)
  localStorage.setItem(FEED_CACHE, JSON.stringify(feed))
  const nextEtag = response.headers.get('ETag')
  if (nextEtag) localStorage.setItem(ETAG_CACHE, nextEtag)
  return feed
}

async function cacheFeedAssets(feed: Feed): Promise<void> {
  for (const item of feed.items) {
    const request = new Request(item.asset_url)
    if ('caches' in window) {
      const cache = await caches.open(MEDIA_CACHE)
      if (await cache.match(request, { ignoreSearch: true })) continue
      const response = await fetch(request)
      if (!response.ok) throw new Error(`图片缓存失败 (${response.status})`)
      await cache.put(request, response.clone())
    } else {
      const response = await fetch(request, { cache: 'force-cache' })
      if (!response.ok) throw new Error(`图片缓存失败 (${response.status})`)
    }
  }
}

export async function completePairing(pairingId: string, key: string, transferSecret: string): Promise<void> {
  const secretDigest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(transferSecret))
  const encryptionKey = await crypto.subtle.importKey('raw', secretDigest, { name: 'AES-GCM' }, false, ['encrypt'])
  const nonce = crypto.getRandomValues(new Uint8Array(12))
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce },
    encryptionKey,
    new TextEncoder().encode(key),
  )
  const response = await fetch(`/api/v1/pairings/${encodeURIComponent(pairingId)}/complete`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${key}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      encrypted_feed_key: base64Url(new Uint8Array(encrypted)),
      nonce: base64Url(nonce),
    }),
  })
  if (!response.ok) throw new Error(response.status === 401 ? '订阅密钥无效' : '配对已失效，请在电视上重新发起')
}

function base64Url(value: Uint8Array): string {
  let binary = ''
  value.forEach(byte => { binary += String.fromCharCode(byte) })
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
